import { inject, InjectionToken } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import type {
  WorkspaceParams,
  WorkspaceParamsWrite,
} from './workspace-location';

/**
 * Where the workspace location is kept. The workspace page keeps it in the
 * URL; the resource picker, which embeds the same lists inside a dialog, keeps
 * it in memory — the page's URL belongs to the page it was opened from.
 *
 * Both behave the same way: a write comes back through `params$`, and that
 * emission — never the write itself — is what moves the workspace.
 */
export interface WorkspaceLocationSource {
  /** The current params, then every change. */
  readonly params$: Observable<WorkspaceParams>;
  write(
    params: Partial<WorkspaceParamsWrite>,
    opts: { replaceUrl: boolean },
  ): void;
}

export const BD_WORKSPACE_LOCATION_SOURCE =
  new InjectionToken<WorkspaceLocationSource>('BD_WORKSPACE_LOCATION_SOURCE');

/** The workspace page: the location is the route's query string. */
export function routerWorkspaceLocationSource(): WorkspaceLocationSource {
  const route = inject(ActivatedRoute);
  const router = inject(Router);
  return {
    params$: route.queryParams as Observable<WorkspaceParams>,
    write: (params, { replaceUrl }) => {
      void router.navigate([], {
        relativeTo: route,
        queryParams: params,
        // Keys this model does not own (none today) survive; its own keys are
        // always all written, so the merge never keeps a stale one.
        queryParamsHandling: 'merge',
        replaceUrl,
      });
    },
  };
}

/** The location in memory — the resource picker, and tests. */
export class MemoryWorkspaceLocationSource implements WorkspaceLocationSource {
  private readonly _params: BehaviorSubject<WorkspaceParams>;

  public readonly params$: Observable<WorkspaceParams>;

  /** Every write, in order — for tests to assert on history behaviour. */
  public readonly writes: {
    params: Partial<WorkspaceParamsWrite>;
    replaceUrl: boolean;
  }[] = [];

  constructor(initial: WorkspaceParams = {}) {
    this._params = new BehaviorSubject<WorkspaceParams>(initial);
    this.params$ = this._params.asObservable();
  }

  public get params(): WorkspaceParams {
    return this._params.value;
  }

  public write(
    params: Partial<WorkspaceParamsWrite>,
    opts: { replaceUrl: boolean },
  ): void {
    this.writes.push({ params, replaceUrl: opts.replaceUrl });
    this.emit({ ...this._params.value, ...params });
  }

  /** Puts params on the "address bar" as if the user had typed a link. */
  public emit(
    params: Partial<Record<string, string | null | undefined>>,
  ): void {
    const next: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        next[key] = value;
      }
    }
    this._params.next(next);
  }
}
